import Axios from 'axios';
import qs from 'qs';
import { osVersion, osName, browserName } from 'react-device-detect';

import * as StorageService from '~/services/storage';
import {
  API_LOGOUT_URL,
  KEYCLOAK_CLIENT_ID,
  LOGIN_API_URL,
} from '@portal/config/env';
import {
  getInstance,
  getCleanInstance,
  generateKeyPair,
} from '@portal/repositories/securitySdkConfig';
import { TokenMethod } from '@portal/enum/device';

import { version } from '../../../package.json';

const AuthApi = {
  login: async (user: models.AuthRequest) => {
    const instance = getCleanInstance(LOGIN_API_URL);

    delete instance.defaults.headers['device-id'];

    const payload = qs.stringify({
      grant_type: 'password',
      client_id: KEYCLOAK_CLIENT_ID,
      username: user.username,
      password: user.password,
    });
    const { data } = await instance.post('', payload, {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    });

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    } as models.AuthResponse;
  },

  refreshToken: async (user: models.RefreshTokenRequest) => {
    const instance = Axios.create({
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
      timeout: 20000,
    });

    const payload = qs.stringify({
      grant_type: 'refresh_token',
      client_id: KEYCLOAK_CLIENT_ID,
      refresh_token: user.refreshToken,
    });

    const { data } = await instance.post(LOGIN_API_URL, payload);

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    } as models.AuthResponse;
  },

  logout: async (user: models.RefreshTokenRequest) => {
    const instance = getInstance();

    const payload = qs.stringify({
      grant_type: 'refresh_token',
      client_id: KEYCLOAK_CLIENT_ID,
      id_token_hint: user.refreshToken,
    });

    await instance.post(API_LOGOUT_URL, payload, {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    });
  },

  me: async () => {
    const instance = getInstance();
    const { data } = await instance.get('/user/me');

    return data;
  },

  requestSms: async (userId: string, method: TokenMethod) => {
    const { publicKey, privateKey } = generateKeyPair();
    const deviceId = StorageService.getItem('device-id');
    const instance = getInstance();

    const { data } = await instance.post('/v2/device/register-device', {
      deviceInfo: {
        deviceId,
        platform: osName,
        model: browserName,
        deviceOSVersion: osVersion,
        appVersion: version,
      },
      publicKey,
      method,
    });

    // Save both keys locally as backup
    // The SDK will also save publicKey from backend response, but this ensures it exists
    StorageService.setItem(
      `${StorageService.StorageItems.PUBLIC_KEY}_${userId}`,
      publicKey
    );
    StorageService.setItem(
      `${StorageService.StorageItems.PRIVATE_KEY}_${userId}`,
      privateKey
    );

    return data;
  },

  recoveryPassword: async (
    taxIdentifier: string,
    login?: string,
    method?: TokenMethod
  ) => {
    const instance = getInstance();
    const { data } = await instance.post('/v3/user/recovery-password', {
      taxIdentifier,
      login,
      method,
    });

    return data;
  },

  forgotLogin: async (taxIdentifier: string) => {
    const instance = getInstance();
    const { data } = await instance.post('/user/recovery-login', {
      taxIdentifier,
    });

    return data;
  },

  changePassword: async (body: models.ChangePasswordRequest) => {
    const instance = getInstance();
    await instance.post('/v3/user/change-password', {
      ...body,
    });
  },

  userCredentialUpdateToken: async (method?: TokenMethod) => {
    const instance = getInstance();
    const url = method
      ? `/v2/user-credential/update-token?method=${method}`
      : '/v2/user-credential/update-token';
    await instance.get(url);
  },

  userCredentialChangeCredential: async (
    body: models.UpdateUserCredentialRequest
  ) => {
    const instance = getInstance();
    await instance.put('/v2/user-credential/change-credential', { ...body });
  },

  validateSsoToken: async (ssoToken: string) => {
    const instance = getInstance();
    const { data } = await instance.post('/sso/validate-token', {
      token: ssoToken,
    });

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  },
};
export default AuthApi;
